import React, { useState, useEffect, useRef } from "react";
import { useCachedImage } from "../hooks/useCachedImage";
import { useNavigate } from "react-router-dom";
import { type User } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useConfirmUnfollow } from "../hooks/useConfirmUnfollow";
import { useToast } from "../contexts/ToastContext";
import {
  XIcon,
  CheckIcon,
  XCircleIcon,
  UsersIcon,
  UserPlusIcon,
} from "./icons/FeatherIcons";
import { usePageTracking } from "../hooks/usePageTracking";
import ConfirmModal from "./ConfirmModal";
import { crashlytics } from "../services/firebase";
import { CommonFunctions } from "../utils/commonUtils";
import { fallbackImage } from "@/utils/getCachedImage";

interface SocialModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "followers" | "following";
  followers: string[];
  following: string[];
  pendingFollowers: string[];
  requestedFollowing: string[];
  isCurrentUserProfile: boolean; // New prop to determine if viewing own profile
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const UserListItem: React.FC<{
  user: User | null;
  onClose: () => void;
  showFollowButton?: boolean;
  showRemoveButton?: boolean;
  onRemoveFollower?: (userId: string) => void;
  onUnfollow?: (userId: string) => void;
  isInFollowersModal?: boolean;
}> = ({
  user,
  onClose,
  showFollowButton = true,
  showRemoveButton = false,
  onRemoveFollower,
  onUnfollow,
  isInFollowersModal = false,
}) => {
  const { currentUser, requestFollow, cancelFollowRequest } = useAuth();
  const { confirmUnfollow, ConfirmUnfollowModal } = useConfirmUnfollow();
  const navigate = useNavigate();

  if (!currentUser || !user) return null;

  const isFollowing = currentUser.following?.includes(user.id);
  const hasRequested = currentUser.requestedFollowing?.includes(user.id);
  const isCurrentUser = currentUser.id === user.id;

  const handleFollowAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser || user.id === currentUser.id) return;
    if (isFollowing) {
      if (onUnfollow) {
        onUnfollow(user.id);
      } else {
        confirmUnfollow(user.id);
      }
    } else if (hasRequested) {
      cancelFollowRequest(user.id);
    } else {
      requestFollow(user.id);
    }
  };

  const handleRemoveFollower = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFollower) {
      onRemoveFollower(user.id);
    }
  };

  const handleNavigate = () => {
    onClose();
    navigate(`/profile/${user.id}`);
  };

  const renderFollowButton = () => {
    if (isCurrentUser || !showFollowButton) return null;

    // Show "Follow Back" only if the user follows you (i.e., you're in their followers list)
    const userFollowsMe = user.following?.includes(currentUser.id);
    let text = userFollowsMe ? "Follow Back" : "Follow";
    let className = "bg-primary text-white text-xs px-3 py-1 rounded-full";

    if (isFollowing) {
      text = "Following";
      className =
        "border border-gray-300 bg-white text-gray-600 text-xs px-3 py-1 rounded-full";
    } else if (hasRequested) {
      text = "Requested";
      className =
        "border border-gray-200 bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full";
    }

    return (
      <button onClick={handleFollowAction} className={className}>
        {text}
      </button>
    );
  };

  return (
    <>
      <div
        onClick={handleNavigate}
        className="flex items-center p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
      >
        {(() => {
          const profileImage = useCachedImage(
            user.profileImageUrl,
            user.username,
            user.id,
          );
          return (
            <img
              loading="lazy"
              onError={handleImageError}
              src={profileImage}
              alt={user.username}
              className="w-12 h-12 rounded-2xl"
            />
          );
        })()}
        <div className="ml-3 flex-1 overflow-hidden">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {user.username}
          </h3>
          <p className="text-xs text-gray-500 truncate">
            {CommonFunctions.calculateAge(user.dateOfBirth)} • {user.location}
          </p>
        </div>
        <div className="ml-2 flex items-center space-x-2">
          {renderFollowButton()}
          {showRemoveButton && !isCurrentUser && (
            <button
              onClick={handleRemoveFollower}
              className="p-1.5 rounded-full hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors"
              title="Remove follower"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {ConfirmUnfollowModal}
    </>
  );
};

const UserRequestItem: React.FC<{
  user: User | null;
  onClose: () => void;
  type: "incoming" | "outgoing";
}> = ({ user, onClose, type }) => {
  const {
    currentUser,
    cancelFollowRequest,
    acceptFollowRequest,
    declineFollowRequest,
  } = useAuth();
  const navigate = useNavigate();

  if (!currentUser || !user) return null;

  const handleNavigate = () => {
    onClose();
    navigate(`/profile/${user.id}`);
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    acceptFollowRequest(user.id);
  };

  const handleDecline = (e: React.MouseEvent) => {
    e.stopPropagation();
    declineFollowRequest(user.id);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    cancelFollowRequest(user.id);
  };

  return (
    <div
      onClick={handleNavigate}
      className="flex items-center p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
    >
      {(() => {
        const profileImage = useCachedImage(
          user.profileImageUrl,
          user.username,
          user.id,
        );
        return (
          <img
            loading="lazy"
            onError={handleImageError}
            src={profileImage}
            alt={user.username}
            className="w-12 h-12 rounded-2xl"
          />
        );
      })()}
      <div className="ml-3 flex-1 overflow-hidden">
        <h3 className="text-sm font-semibold text-gray-900 truncate">
          {user.username}
        </h3>
        <p className="text-xs text-gray-500 truncate">
          {CommonFunctions.calculateAge(user.dateOfBirth)} • {user.location}
        </p>
      </div>
      <div className="ml-2 flex space-x-2">
        {type === "incoming" ? (
          <>
            <button
              onClick={handleAccept}
              className="bg-primary text-white p-2 rounded-full hover:bg-primary/90 transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleDecline}
              className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-200 transition-colors"
            >
              <XCircleIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={handleCancel}
            className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

const SocialModal: React.FC<SocialModalProps> = ({
  isOpen,
  onClose,
  type,
  followers,
  following,
  pendingFollowers,
  requestedFollowing,
  isCurrentUserProfile,
}) => {
  usePageTracking(
    isOpen
      ? type === "followers"
        ? "FollowersOpened"
        : "FollowingOpened"
      : type === "followers"
        ? "FollowersClosed"
        : "FollowingClosed",
    "SocialModal"
  );
  const { getUserById, removeFollower } = useAuth();
  const { confirmUnfollow, ConfirmUnfollowModal } = useConfirmUnfollow();
  const { addToast } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"main" | "requests">("main");
  const [users, setUsers] = useState<User[]>([]);
  const [requestUsers, setRequestUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab("main");
      fetchUsers();
    }
  }, [isOpen, type]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      // Fetch main list users
      const mainUserIds = type === "followers" ? followers : following;
      const mainUserPromises = mainUserIds.map((id) => getUserById(id));
      const mainUserResults = await Promise.all(mainUserPromises);
      const validMainUsers = mainUserResults.filter(
        (user): user is User => user !== null,
      );

      // Fetch request users
      const requestUserIds =
        type === "followers" ? pendingFollowers : requestedFollowing;
      const requestUserPromises = requestUserIds.map((id) => getUserById(id));
      const requestUserResults = await Promise.all(requestUserPromises);
      const validRequestUsers = requestUserResults.filter(
        (user): user is User => user !== null,
      );

      setUsers(validMainUsers);
      setRequestUsers(validRequestUsers);
    } catch (error) {
      crashlytics.logError(
        `Error fetching users. Error: ${JSON.stringify(error)}`,
        "SocialModal.tsx",
        error,
      );
      setUsers([]);
      setRequestUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFollower = async (followerId: string) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove Follower",
      message:
        "Remove this follower? They will be notified and will no longer follow you.",
      onConfirm: async () => {
        try {
          await removeFollower(followerId);
          // Update local state to remove the user immediately
          setUsers((prev) => prev.filter((user) => user.id !== followerId));
        } catch (error) {
          crashlytics.logError(
            `Error removing follower. Error: ${JSON.stringify(error)}`,
            "SocialModal.tsx",
            error,
          );
          addToast("Failed to remove follower. Please try again.", "error");
        }
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUnfollow = (userId: string) => {
    confirmUnfollow(userId, () => {
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    });
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const mainTitle = type === "followers" ? "Followers" : "Following";
  const requestTitle =
    type === "followers" ? "Follow Requests" : "Sent Requests";

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-bold text-gray-900">
            {activeTab === "main" ? mainTitle : requestTitle}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab Navigation - Only show if current user profile (has both tabs) */}
        {isCurrentUserProfile && (
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab("main")}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === "main"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <UsersIcon className="w-4 h-4 inline mr-2" />
              {mainTitle}
              {users.length > 0 && (
                <span className="ml-1 bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                  {users.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("requests")}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === "requests"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <UserPlusIcon className="w-4 h-5 inline mr-2" />
              {requestTitle}
              {requestUsers.length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                  {requestUsers.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto max-h-96">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {activeTab === "main" ? (
                users.length > 0 ? (
                  <div className="py-2">
                    {users.map((user) => (
                      <UserListItem
                        key={user?.id || Math.random()}
                        user={user}
                        onClose={onClose}
                        showRemoveButton={
                          isCurrentUserProfile && type === "followers"
                        }
                        onRemoveFollower={handleRemoveFollower}
                        onUnfollow={
                          isCurrentUserProfile ? handleUnfollow : undefined
                        }
                        isInFollowersModal={type === "followers"}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-12 px-4 text-center">
                    <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">
                      {type === "followers"
                        ? "No followers yet"
                        : "Not following anyone yet"}
                    </p>
                  </div>
                )
              ) : isCurrentUserProfile && requestUsers.length > 0 ? (
                <div className="py-2">
                  {requestUsers.map((user) => (
                    <UserRequestItem
                      key={user?.id || Math.random()}
                      user={user}
                      onClose={onClose}
                      type={type === "followers" ? "incoming" : "outgoing"}
                    />
                  ))}
                </div>
              ) : isCurrentUserProfile ? (
                <div className="py-12 px-4 text-center">
                  <UserPlusIcon className="w-13 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    {type === "followers"
                      ? "No pending follow requests"
                      : "No sent requests"}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {ConfirmUnfollowModal}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Remove"
        confirmStyle="danger"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default SocialModal;
